import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import moment from 'moment';

const ChatMessage = ({ message, order, playingAudio, setPlayingAudio, setPlayingMessageId, playingMessageId }) => {
  const audioRef = useRef(new Audio(message.recording_file));
  const isPlaying = playingMessageId === message.id;

  useEffect(() => {
    const audio = audioRef.current;

    // Stop audio when it ends & reset icon
    const handleEnded = () => {
      setPlayingMessageId(null);
      setPlayingAudio(null);
    };

    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [setPlayingAudio]);

  const handlePlayPause = () => {
    // If another audio is playing, stop it before playing this one
    if (playingAudio && playingAudio !== audioRef.current) {
      playingAudio.pause();
      setPlayingAudio(null);
    }

    if (isPlaying) {
      audioRef.current.pause();
      setPlayingAudio(null);
      setPlayingMessageId(null);
    } else {
      audioRef.current.play();
      setPlayingMessageId(message.id);
      setPlayingAudio(audioRef.current);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: message.type === "outgoing" ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 1,
        marginBottom: "10px",
        transition: "background-color 0.3s ease, box-shadow 0.3s ease",
        "&:hover": { cursor: "pointer", backgroundColor: "#f0f0f0" },
      }}
    >
      {message.type === "outgoing" ?
        message.agent_pic ?
          <Avatar src={message.agent_pic} alt="Profile Image" sx={{ width: 38.71, height: 37.66, cursor: "pointer" }} />
          :
          <Avatar sx={{ width: 40, height: 40 }}>{message.agent_name ? message.agent_name.charAt(0) : 'A'}</Avatar>
        :
        <Avatar sx={{ width: 40, height: 40 }}>{order?.name ? order.name.charAt(0) : 'U'}</Avatar>
      }

      <Box sx={{ maxWidth: "70%", textAlign: "left" }}>
        <Box sx={{ display: "flex", flexDirection: message.type === "outgoing" ? "row-reverse" : "row", justifyContent: "space-between" }}>
          <Typography variant="body2" sx={{ fontSize: "13px", fontWeight: 500, color: "#515151" }}>
            {message.type === "outgoing" ? message.agent_name : order.name}
          </Typography>
          <Typography variant="body3" sx={{ fontSize: "13px", margin: '0 10px' }}>
            {moment(message.created_at).format("MMMM DD YYYY, h:mm:ss a")}
          </Typography>
        </Box>

        {/* Text Message */}
        {(message.sms_type === "sms" || !message.sms_type) ? (
          <Box sx={{ 
            backgroundColor:  message.type === "outgoing" ? "#0b6699" : "#fff", 
            borderRadius: message.type === "outgoing" ? "15px 0 15px 15px" : "0 15px 15px 15px", 
            padding: "10px", 
            boxShadow: 2,
            color:  message.type === "outgoing" ? "#fff" : "black"
            }}>
            <Typography variant="body2" sx={{ fontSize: "12px", fontWeight: 400, color:  message.type === "outgoing" ? "#fff" : "black" }}>{message.body}</Typography>
          </Box>
        ) :

          /* Audio Message */
          (message.sms_type === "audio" && message.recording_file) ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                padding: "10px",
                backgroundColor: message.type === "outgoing" ? "#0b6699" : "#fff",
                borderRadius: message.type === "outgoing" ? "15px 0 15px 15px" : "0 15px 15px 15px",
                color:  message.type === "outgoing" ? "#fff" : "black",
              }}
            >
              {isPlaying ? (
                <PauseCircleIcon onClick={handlePlayPause} sx={{ cursor: 'pointer' }} />
              ) : (
                <PlayCircleIcon onClick={handlePlayPause} sx={{ cursor: 'pointer' }} />
              )}
              <Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 400, color:  message.type === "outgoing" ? "#fff" : "black" }}>{message.status}</Typography>
              <Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 400, color:  message.type === "outgoing" ? "#fff" : "black" }}>{message.duration} seconds</Typography>
            </Box>
          ) :

            /* Status Message */
            (message.sms_type === "status") ? (
              <Box sx={{ backgroundColor: "#0b6699", padding: "10px", borderRadius: message.type === "outgoing" ? "15px 0 15px 15px" : "0 15px 15px 15px" }}>
                <Typography variant="body2" color="#fff" sx={{ fontSize: "13px", fontWeight: 600 }}>{message.status}</Typography>
                <Typography variant="body2" color="#fff" sx={{ fontSize: "13px", fontWeight: 400 }}>{message.statusDetails}</Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontSize: "12px", fontWeight: 400 }}>No Audio Recording</Typography>
            )}
      </Box>
    </Box>
  );
};

const ChatWindow = ({ messages, order }) => {
  const [playingAudio, setPlayingAudio] = useState(null);
  const [playingMessageId, setPlayingMessageId] = useState(null);

  return (
    <Box sx={{ width: "100%" }}>
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} order={order} playingMessageId={playingMessageId} setPlayingMessageId={setPlayingMessageId} playingAudio={playingAudio} setPlayingAudio={setPlayingAudio} />
      ))}
    </Box>
  );
};

export default ChatWindow;
